import { useMemo, useState } from "react";

import {
  hashKey,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";

import { apiClient } from "../lib/apiClient";
import {
  ApiRequestError,
  requestErrorDescription,
  variableDeleteConflictDetail,
} from "../lib/errors";
import {
  fetchAllVariablePersonas,
  fetchAllVariableScenarios,
} from "../lib/variableScopeOptions";
import { queryKeys } from "../lib/queryKeys";
import {
  parseVariablesSearch,
  type VariablesSearch,
} from "../lib/routerSearch";
import {
  mergeSearch,
  useTypedNavigate,
  useTypedSearch,
} from "../lib/typedRouter";
import { useListQuery } from "../lib/useListQuery";
import { useVariableDeclarations } from "../lib/useVariableDeclarations";
import {
  withWorkingScopeSearch,
  type WorkingScopeSearch,
} from "../lib/workingScope";
import { useToast } from "../ui";
import type { VariableEditorProps } from "./variables/VariableEditor";
import {
  toVariableCreate,
  toVariablePatch,
  type Variable,
  type VariableFormState,
  type VariablePatch,
} from "./variables/variableFormState";
import { VariablesView } from "./variables/VariablesView";

type VariablesSearchState = VariablesSearch & WorkingScopeSearch;

export function Variables() {
  const params = useParams({ strict: false });
  const agentId = typeof params.agentId === "string" ? params.agentId : "";
  const navigate = useTypedNavigate<VariablesSearchState>();
  const search = useTypedSearch(withWorkingScopeSearch(parseVariablesSearch));
  const queryClient = useQueryClient();
  const toast = useToast();
  const [deleteVariableId, setDeleteVariableId] = useState<string | null>(null);

  const patchSearch = (patch: Partial<VariablesSearch>, replace: boolean) => {
    void navigate({
      replace,
      search: mergeSearch<VariablesSearchState>(patch),
    });
  };
  const closeEditor = () => {
    patchSearch(
      {
        key: undefined,
        new: undefined,
        scope_id: undefined,
        scope_type: undefined,
        variable: undefined,
      },
      true,
    );
  };

  const agent = useQuery({
    enabled: agentId.length > 0,
    queryKey: queryKeys.agents.detail(agentId),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/agents/{id}", {
        params: { path: { id: agentId } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
  });
  const declarations = useVariableDeclarations(agentId);
  const personas = useQuery({
    enabled: agentId.length > 0,
    queryKey: queryKeys.personas.allForAgent(agentId),
    queryFn: ({ signal }) => fetchAllVariablePersonas(agentId, signal),
  });
  const scenarios = useQuery({
    enabled: agentId.length > 0,
    queryKey: queryKeys.scenarios.allForAgent(agentId),
    queryFn: ({ signal }) => fetchAllVariableScenarios(agentId, signal),
  });
  const deleteReferences = useListQuery("/v1/variables/{id}/references", {
    enabled: deleteVariableId !== null,
    pathParams: { id: deleteVariableId ?? "" },
    queryKey: queryKeys.variables.references(deleteVariableId ?? "pending"),
    staleTime: 0,
  });

  const variableDetail = useQuery({
    enabled: search.variable !== undefined,
    queryKey: queryKeys.variables.detail(search.variable ?? "pending"),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/variables/{id}", {
        params: { path: { id: search.variable ?? "" } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    initialData: () =>
      declarations.data?.find((variable) => variable.id === search.variable),
    initialDataUpdatedAt: () => declarations.dataUpdatedAt,
  });

  const saveVariable = useMutation({
    mutationFn: async ({
      state,
      variable,
    }: {
      state: VariableFormState;
      variable: Variable | null;
    }) => {
      if (variable === null) {
        const { data, error } = await apiClient.POST("/v1/variables", {
          body: toVariableCreate(state),
        });
        if (error) {
          throw new ApiRequestError(error);
        }
        return data;
      }
      const { data, error } = await apiClient.PATCH("/v1/variables/{id}", {
        // Every VariablePatch field carries `default: null`, so
        // openapi-typescript emits all of them non-optional. The wire contract
        // is pydantic `fields_set`: an omitted field is untouched, and core
        // mints a revision for any content field that is sent. A sparse body
        // is therefore correct and the generated type cannot express it.
        body: toVariablePatch(state, variable) as VariablePatch,
        params: { path: { id: variable.id } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    onSuccess: async (saved) => {
      queryClient.setQueryData(queryKeys.variables.detail(saved.id), saved);
      closeEditor();
      toast.success("Variable saved");
      await queryClient.invalidateQueries({
        queryKey: queryKeys.variables.all,
      });
    },
  });

  const deleteVariable = useMutation({
    mutationFn: async (variable: Variable) => {
      const { error } = await apiClient.DELETE("/v1/variables/{id}", {
        params: { path: { id: variable.id } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return variable;
    },
    onSuccess: async () => {
      setDeleteVariableId(null);
      toast.success("Variable deleted");
      await queryClient.invalidateQueries({
        queryKey: queryKeys.variables.all,
      });
    },
  });

  const scopeNames = useMemo(() => {
    const names = new Map<string, string>();
    if (agent.data !== undefined) {
      names.set(agent.data.id, agent.data.display_name);
    }
    for (const persona of personas.data ?? []) {
      names.set(persona.id, persona.display_name);
    }
    for (const scenario of scenarios.data ?? []) {
      names.set(scenario.id, scenario.display_name);
    }
    return names;
  }, [agent.data, personas.data, scenarios.data]);

  const editingVariable = search.variable
    ? (variableDetail.data ?? null)
    : null;
  const editor: VariableEditorProps | null =
    search.new === 1 || editingVariable !== null
      ? {
          agentId,
          error: saveVariable.isError
            ? requestErrorDescription(saveVariable.error)
            : null,
          isPending: saveVariable.isPending,
          initialKey: search.key,
          initialScopeId: search.scope_id,
          initialScopeType: search.scope_type,
          onCancel: closeEditor,
          onSubmit: async (state) => {
            await saveVariable.mutateAsync({
              state,
              variable: editingVariable,
            });
          },
          personaOptions: (personas.data ?? []).map((persona) => ({
            id: persona.id,
            label: persona.display_name,
          })),
          scenarioOptions: (scenarios.data ?? []).map((scenario) => ({
            id: scenario.id,
            label: scenario.display_name,
          })),
          variable: editingVariable,
        }
      : null;

  const errorMessage =
    (declarations.isError
      ? requestErrorDescription(declarations.error)
      : undefined) ??
    (personas.isError ? requestErrorDescription(personas.error) : undefined) ??
    (scenarios.isError
      ? requestErrorDescription(scenarios.error)
      : undefined) ??
    (agent.isError ? requestErrorDescription(agent.error) : undefined) ??
    (variableDetail.isError
      ? requestErrorDescription(variableDetail.error)
      : undefined);

  return (
    <VariablesView
      deleteConflict={variableDeleteConflictDetail(deleteVariable.error)}
      deleteError={
        deleteVariable.isError
          ? requestErrorDescription(deleteVariable.error)
          : null
      }
      deleteReferences={deleteReferences}
      deleteVariableId={deleteVariableId}
      editor={editor}
      errorMessage={errorMessage}
      isDeletePending={deleteVariable.isPending}
      isPending={
        declarations.isPending ||
        personas.isPending ||
        scenarios.isPending ||
        agent.isPending
      }
      onCancelDelete={() => {
        deleteVariable.reset();
        setDeleteVariableId(null);
      }}
      onConfirmDelete={(variable) => {
        deleteVariable.mutate(variable);
      }}
      onOpenCreate={() => {
        saveVariable.reset();
        patchSearch(
          {
            key: undefined,
            new: 1,
            scope_id: undefined,
            scope_type: undefined,
            variable: undefined,
          },
          false,
        );
      }}
      onOpenEdit={(variableId) => {
        saveVariable.reset();
        patchSearch(
          {
            key: undefined,
            new: undefined,
            scope_id: undefined,
            scope_type: undefined,
            variable: variableId,
          },
          false,
        );
      }}
      onStartDelete={(variable) => {
        deleteVariable.reset();
        setDeleteVariableId(variable.id);
      }}
      scopeNames={scopeNames}
      variables={{
        fetchNextPage: () => undefined,
        hasNextPage: false,
        isFetchingNextPage: false,
        isPending: declarations.isPending,
        // Same derivation as useListQuery: the paginate-all loader is one
        // query, so its restore key is the hash of that query key.
        listStateKey: hashKey(queryKeys.variables.allForAgent(agentId)),
        items: [...(declarations.data ?? [])].sort((left, right) =>
          left.key.localeCompare(right.key),
        ),
      }}
    />
  );
}
